'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';

export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] flex-shrink-0 bg-card border-r border-border p-10 relative overflow-hidden">
        <div className="absolute inset-0 hero-grid-bg opacity-50 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-5 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #F5C518 0%, transparent 70%)' }} />

        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-2.5 mb-12">
            <AppLogo size={36} />
            <span className="font-bold text-lg text-foreground">FanFinite</span>
          </Link>

          <div className="space-y-6">
            <div className="text-5xl">🍍</div>
            <h2 className="text-3xl font-extrabold text-foreground leading-tight">
              Your Detective<br />
              <span className="gradient-text-primary">Career Awaits</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Join thousands of fans competing in the Psych Case Files. 
              Earn your Detective Agency credentials and prove your fandom.
            </p>
          </div>

          {/* Feature bullets */}
          <div className="mt-10 space-y-4">
            {[
              { icon: '🏅', text: 'Earn XP and climb 6 detective ranks' },
              { icon: '🆔', text: 'Get your unique Detective Agency ID Card' },
              { icon: '🏆', text: 'Compete on global leaderboards' },
              { icon: '🎮', text: '7 game categories across Psych World' },
            ]?.map((item, i) => (
              <div key={`feature-bullet-${i}`} className="flex items-center gap-3">
                <span className="text-lg">{item?.icon}</span>
                <span className="text-sm text-muted-foreground">{item?.text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-xs text-muted-foreground">
          Fan-made platform · Not affiliated with any official IP holders
        </p>
      </div>
      {/* Right auth panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Mobile logo */}
        <Link href="/" className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={28} />
          <span className="font-bold text-base text-foreground">FanFinite</span>
        </Link>

        <div className="w-full max-w-md">
          {/* Tab switcher */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('signup')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeTab === 'signup' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {activeTab === 'login' ? (
            <LoginForm onSwitchToSignup={() => setActiveTab('signup')} />
          ) : (
            <SignupForm onSwitchToLogin={() => setActiveTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}